import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import assignmentV2ResultsUtils from '../assignmentV2Results.utils';
import {Table, Column} from 'sg/Tables/Table/Table.react';
import {renderDuration} from 'lib/timeUtils';
import AssignmentCompletionStatus from 'client/Classrooms/shared/AssignmentCompletionStatus/AssignmentCompletionStatus.react';
import PerformanceIndicator from 'generic/PerformanceIndicator/PerformanceIndicator.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {stats} from 'lib/StatsUtil';
import {getSubmissionStatus} from './assignmentResultsStudentsList.utils';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import appStore from 'client/AppStore';
import './assignment-results-students-list.scss';

export default class AssignmentResultsStudentsList extends React.Component {
  static propTypes = {
    activeSectionId: PropTypes.string
  };

  getQuestionCounts(student, activeSection) {
    const correctCount = student.getMeta().getSectionCountOfCorrectGuesses();
    const incorrectCount = student.getMeta().getSectionCountOfIncorrectGuesses();
    const numberAnswered = correctCount + incorrectCount;
    const questionCount = activeSection.getMeta().getCountOfQuestions();
    return {
      correctCount,
      incorrectCount,
      questionCount,
      numberAnswered
    };
  }

  render() {
    if (!assignmentV2ResultsUtils.getAssignmentQuery().isResourcePopulated()) {
      return <LoadingIndicator />;
    }
    const assignment = assignmentV2ResultsUtils.getAssignmentQuery().getResource();
    const activeSection = assignment
      .getSections()
      .find((section) => section.getId() === this.props.activeSectionId);
    const activeSectionId = activeSection.getId();
    const students = activeSection.getStudents();
    return (
      <Table
        data={students}
        storeName={`AssignmentResultsStudentsListStore/${activeSectionId}`}
        rowPropsFunc={makeLinkRowProps((student) => {
          return `${appStore.routerProps.location.pathname}/student/${student.getId()}`;
        })}
      >
        <Column
          sortable
          heading='Name'
          sortBy={(student) => student.getLastName().toLowerCase()}
          content={(student) => (
            <span className='assignment-results-students-list-name' data-gtu={student.getId()}>
              {student.getFullName({formal: true})}
            </span>
          )}
        />
        <Column
          sortable
          sortBy={(student) => {
            const sectionData = student.getSectionRelationships().get(activeSectionId);
            const submittedAt = sectionData.getSubmittedAt();
            if (!submittedAt) {
              return 0;
            }
            return moment(submittedAt).isBefore(assignment.getDueDate()) ? 2 : 1;
          }}
          heading={
            <span>
              Submission
              <br />
              Status
            </span>
          }
          content={(student) => (
            <SubmissionStatus assignment={assignment} section={activeSection} student={student} />
          )}
        />
        <Column
          sortable
          heading='Time Spent'
          sortBy={(student) => student.getMeta().getSectionTimeSpent()}
          content={(student) => {
            const timeSpent = moment.duration(student.getMeta().getSectionTimeSpent());
            const formatted = renderDuration(timeSpent, {style: 'hh:mm:ss'});
            return `${formatted}s`;
          }}
        />
        <Column
          sortable
          heading={
            <span>
              Answered
              <br />
              Correctly
            </span>
          }
          sortBy={(student) => this.getQuestionCounts(student, activeSection).correctCount}
          content={(student) => {
            const {
              correctCount,
              incorrectCount,
              numberAnswered,
              questionCount
            } = this.getQuestionCounts(student, activeSection);
            return (
              <div className='assignment-results-students-list-correct'>
                <div className='assignment-results-students-list-correct__count'>
                  {correctCount} / {questionCount}
                </div>
                <AssignmentCompletionStatus
                  tiny
                  numberSubmitted={correctCount}
                  numberSubmittedLate={incorrectCount}
                  numberNotSubmitted={questionCount - numberAnswered}
                />
              </div>
            );
          }}
        />
        <Column
          sortable
          heading='Performance'
          sortBy={(student) => {
            const {correctCount, questionCount, numberAnswered} = this.getQuestionCounts(
              student,
              activeSection
            );
            if (numberAnswered === 0) {
              return -1;
            }
            const isSubmitted =
              student
                .getSectionRelationships()
                .get(activeSection.getId())
                .getSubmittedAt() !== null;
            return isSubmitted ? stats.percentage(correctCount, questionCount) : 0;
          }}
          content={(student) => {
            const {correctCount, questionCount, numberAnswered} = this.getQuestionCounts(
              student,
              activeSection
            );
            const isSubmitted =
              student
                .getSectionRelationships()
                .get(activeSectionId)
                .getSubmittedAt() !== null;
            return (
              <PerformanceIndicator
                gtgTag={student.getId()}
                isUnavailable={!isSubmitted}
                numberCorrect={correctCount}
                total={isSubmitted ? questionCount : numberAnswered}
              />
            );
          }}
        />
      </Table>
    );
  }
}

class SubmissionStatus extends React.PureComponent {
  static propTypes = {
    student: PropTypes.any,
    section: PropTypes.any,
    assignment: PropTypes.any
  };

  render() {
    const status = getSubmissionStatus(
      this.props.student,
      this.props.section,
      this.props.assignment
    );
    return (
      <div
        className={classnames(
          'assignment-submission-status',
          `assignment-submission-status--${status.classModifier}`
        )}
      >
        {status.display}
      </div>
    );
  }
}
